import { useMemo } from "react";
import { TaskStatusMultiSelect } from "@components/tasks/TaskStatusSelect/TaskStatusMultiSelect";
import { TaskGroupBy, TaskStatus, TaskType } from "@common/types/task";
import { TaskListQuery } from "@common/contracts/task.contract";
import { FiltersTypes } from "@common/types/filters/general";
import { DateRangeFilterNext } from "@design-system/DateRangeFilter/DateRangeFilterNext";
import {
    toDateOnlyDateRange,
    toLocalDateRange,
} from "@design-system/DateRangeFilter/DateRangeFilterNext.lib";
import { Picker } from "@design-system/Picker/Picker.lib";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { ExcludeEmptyMockEntitiesMode } from "@hooks/useEntitiesContexts";
import {
    FilterTabs,
    FiltersChangeFn,
} from "@components/pageHeader/filters/lib";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { PlusIcon } from "@icons";
import { useOverlays } from "contexts/OverlayContext";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { usePinnedDatePresets } from "@hooks/usePinnedDatePresets";
import { useScope } from "@state/scopeContext.lib";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import { SUGGESTED_PRESETS } from "@helpers/date";
import { HStack } from "@design-system/HStack/HStack";
import styles from "./TasksPage.module.scss";
import {
    isTaskGroupByAvailable,
    TaskListCapabilities,
} from "./taskListCapabilities";

const GROUP_BY_LABELS: Record<TaskGroupBy, string> = {
    [TaskGroupBy.STATUS]: "By status",
    [TaskGroupBy.PRIORITY]: "By priority",
    [TaskGroupBy.ASSIGNEE]: "By assignee",
    [TaskGroupBy.DUE_DATE]: "By due date",
    [TaskGroupBy.TASK_GROUP]: "By task group",
    [TaskGroupBy.CLOSE_STAGE]: "By stage",
    [TaskGroupBy.CLOSE]: "By service",
    [TaskGroupBy.CLIENT]: "By client",
};

interface TasksListHeaderProps {
    filters: TaskListQuery;
    setFilters: (
        filters: TaskListQuery | ((prev: TaskListQuery) => TaskListQuery),
    ) => void;
    selectedTaskId: string | null;
    overlayFilters: FiltersTypes;
    onOverlayFiltersChange: FiltersChangeFn;
    taskAvailableFilters: FilterTabs[];
    hasCloseStages: boolean;
    hasCloses: boolean;
    capabilities: TaskListCapabilities;
}

export const TasksListHeader = ({
    filters,
    setFilters,
    selectedTaskId,
    overlayFilters,
    onOverlayFiltersChange,
    taskAvailableFilters,
    hasCloseStages,
    hasCloses,
    capabilities,
}: TasksListHeaderProps) => {
    const { openOverlay } = useOverlays();
    const { organizationId } = useScope();
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const groupBy = filters.groupBy ?? TaskGroupBy.STATUS;
    const search = filters.search ?? "";

    const groupByItems = useMemo(
        () =>
            Object.values(TaskGroupBy)
                .filter((value) =>
                    isTaskGroupByAvailable({
                        groupBy: value,
                        capabilities,
                        hasOrganization: !!organizationId,
                        hasCloseStages,
                        hasCloses,
                    }),
                )
                .map((value) => ({ value, label: GROUP_BY_LABELS[value] })),
        [organizationId, hasCloseStages, hasCloses, capabilities],
    );

    const { pinnedPresets, onTogglePin } = usePinnedDatePresets({
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.PINNED_DATE_PRESETS_TASKS_CACHE_KEY,
        defaults: SUGGESTED_PRESETS.accounting,
    });

    // Opens a create form the server can only refuse on save, after the user
    // has filled it in (PRO-11345).
    const handleCreateTask = () => {
        if (refuseIfViewOnly()) {
            return;
        }
        openOverlay("createTask", { type: TaskType.QUESTION });
    };

    return (
        <TablePageHeader
            isFullPage
            search={search}
            onSearchChange={
                !selectedTaskId
                    ? (value) =>
                          setFilters((prev) => ({ ...prev, search: value }))
                    : undefined
            }
            className={styles.tasksListHeaderContainer}
            searchKeyboardShortcuts
            filters={overlayFilters}
            onFiltersChange={onOverlayFiltersChange}
            availableFilters={selectedTaskId ? [] : taskAvailableFilters}
            hasTaskCreatedByClientOption={capabilities.canFilterByClientCreator}
            hasTaskAssignedToClientOption={
                capabilities.canFilterByClientAssignee
            }
            hasTaskAssigneeOrgMembersOnly={capabilities.isCrossWorkspace}
        >
            <TaskStatusMultiSelect
                selectedStatuses={
                    !filters.status || !filters.status.length
                        ? [
                              TaskStatus.TODO,
                              TaskStatus.IN_PROGRESS,
                              TaskStatus.DONE,
                              TaskStatus.CANCELLED,
                          ]
                        : filters.status
                }
                onChange={(statuses) =>
                    setFilters((prev) => ({ ...prev, status: statuses }))
                }
            />
            <HStack align="center" gap={8}>
                {!selectedTaskId && (
                    <>
                        <div className={styles.buttonGroup}>
                            <DateRangeFilterNext
                                buttonClass={styles.dateRangeFilter}
                                dateRange={toDateOnlyDateRange({
                                    start: filters.start,
                                    end: filters.end,
                                })}
                                onChange={(value) => {
                                    const localRange = toLocalDateRange(value);
                                    setFilters((prev) => ({
                                        ...prev,
                                        start: localRange.start,
                                        end: localRange.end,
                                    }));
                                }}
                                pinnedPresets={pinnedPresets}
                                onTogglePin={onTogglePin}
                            />
                            <Picker.Select
                                items={groupByItems}
                                value={groupBy}
                                onValueChange={(value) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        groupBy: value ?? TaskGroupBy.STATUS,
                                    }))
                                }
                                renderValue={() => GROUP_BY_LABELS[groupBy]}
                                trigger={
                                    <Picker.Button
                                        variant="primary"
                                        size="md"
                                        className={styles.groupBySelect}
                                    />
                                }
                            />
                        </div>
                        {capabilities.canFilterByEntity && (
                            <EntityFilterCombobox
                                filters={filters}
                                onChange={(value) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        entityIds: value.entityIds,
                                    }))
                                }
                                excludeEmptyMockEntities={
                                    ExcludeEmptyMockEntitiesMode.NEVER
                                }
                                showLabel={false}
                            />
                        )}
                    </>
                )}
                {capabilities.canCreateTasks && (
                    <WithHotkey
                        hotKey="alt+n"
                        hotKeyOptions={{
                            preventDefault: true,
                            description: "Create task",
                        }}
                    >
                        {selectedTaskId ? (
                            <Button
                                variant="primary"
                                size="md"
                                iconOnly
                                aria-label="Create task"
                                onClick={handleCreateTask}
                            >
                                <PlusIcon className="icon-color-gray-900" />
                            </Button>
                        ) : (
                            <Button
                                variant="primary"
                                size="md"
                                onClick={handleCreateTask}
                            >
                                Create task
                            </Button>
                        )}
                    </WithHotkey>
                )}
            </HStack>
        </TablePageHeader>
    );
};
